/**
 * 버전 체크 훅 - 새 빌드 배포 시 자동 새로고침 + PC 업데이트 안내 + 버전 표시
 *
 * 브릿지앱 WebView는 페이지 로드가 끝날 때 네이티브 immersive mode를 복원하므로
 * 웹 Fullscreen API나 사용자 터치가 필요하지 않다. 일반 PC 브라우저에서만
 * 자동 새로고침 완료 안내를 표시하고, 확인 시 전체화면으로 전환하지 않는다.
 */

import { useEffect, useRef, useState } from 'react';

import { isBridgeApp } from '../utils/isBridgeApp';

const CHECK_INTERVAL = 60 * 1000; // 1분
const UPDATE_NOTICE_KEY = 'tools_update_notice_pending';
const LEGACY_FULLSCREEN_KEY = 'was_fullscreen';

// 판정 본체는 utils/isBridgeApp.js 단일 출처로 옮겼다(2026-08-06 — 전체화면 게이트가
// 같은 판정을 5곳 더 쓰게 되면서). 여기서 **재export** 하는 이유는 기존 import 경로
// (`useVersionCheck.test.js`)를 그대로 살리기 위해서다 — 호출부를 흔들지 않는다.
export { isBridgeApp };

export function shouldShowUpdateNotice(pending, userAgent) {
  return pending && !isBridgeApp(userAgent);
}

function formatVersion(ts) {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yy}/${mm}/${dd} ${hh}:${mi}`;
}

export default function useVersionCheck() {
  const currentVersionRef = useRef(null);
  const [buildTime, setBuildTime] = useState('');
  const [showUpdateNotice, setShowUpdateNotice] = useState(false);

  // 앱 로드 시 PC 브라우저에만 업데이트 완료 안내 표시.
  // 과거 버전이 남긴 was_fullscreen 값도 1회 소비해 브릿지앱에 팝업이 남지 않게 한다.
  useEffect(() => {
    const pending =
      localStorage.getItem(UPDATE_NOTICE_KEY) === 'true' ||
      localStorage.getItem(LEGACY_FULLSCREEN_KEY) === 'true';
    localStorage.removeItem(UPDATE_NOTICE_KEY);
    localStorage.removeItem(LEGACY_FULLSCREEN_KEY);
    // React 18 개발 StrictMode는 effect를 한 번 더 실행한다. 두 번째 실행에서
    // 이미 소비한 플래그(false)로 안내 상태를 다시 끄지 않도록 true일 때만 설정한다.
    if (shouldShowUpdateNotice(pending)) {
      setShowUpdateNotice(true);
    }
  }, []);

  // PC 브라우저 업데이트 완료 안내. 확인은 팝업만 닫고 전체화면으로 전환하지 않는다.
  useEffect(() => {
    if (!showUpdateNotice) return;

    const overlay = document.createElement('div');
    overlay.id = 'update-complete-overlay';
    const chars = ['zl_baba.png','zl_coco.png','zl_sandy.png','zl_redy.png','zl_lazy.png','zl_dummer.png'];
    const randomChar = chars[Math.floor(Math.random() * chars.length)];

    overlay.innerHTML = `
      <div style="
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        cursor: pointer;
        -webkit-user-select: none;
        user-select: none;
        -webkit-tap-highlight-color: transparent;
      ">
        <div style="
          text-align: center;
          color: #fff;
          padding: 40px;
        ">
          <img src="/characters/${randomChar}" alt="주렁 프렌즈" style="
            width: 120px;
            height: 120px;
            border-radius: 50%;
            object-fit: cover;
            border: 4px solid #fff;
            box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            margin-bottom: 20px;
          " />
          <div style="font-family: 'Jua', sans-serif; font-size: 28px; margin-bottom: 12px;">
            업데이트 완료
          </div>
          <div style="font-size: 18px; color: #ccc; margin-bottom: 32px;">
            새 버전으로 자동 새로고침했습니다
          </div>
          <div style="
            display: inline-block;
            padding: 16px 48px;
            background: linear-gradient(135deg, #4CAF50, #388E3C);
            border-radius: 16px;
            font-family: 'Jua', sans-serif;
            font-size: 24px;
            color: #fff;
            box-shadow: 0 4px 16px rgba(76, 175, 80, 0.4);
          ">
            확인
          </div>
        </div>
      </div>
    `;

    overlay.addEventListener('click', () => {
      setShowUpdateNotice(false);
      overlay.remove();
    });

    document.body.appendChild(overlay);

    return () => {
      const el = document.getElementById('update-complete-overlay');
      if (el) el.remove();
    };
  }, [showUpdateNotice]);

  // 버전 폴링
  useEffect(() => {
    let timer = null;

    const fetchVersion = async () => {
      try {
        const res = await fetch('/version.json?t=' + Date.now(), {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = await res.json();
        const newVersion = data.v;

        if (currentVersionRef.current === null) {
          currentVersionRef.current = newVersion;
          setBuildTime(formatVersion(newVersion));
        } else if (currentVersionRef.current !== newVersion) {
          console.log('[VersionCheck] 새 버전 감지, 새로고침합니다.', currentVersionRef.current, '→', newVersion);
          if (isBridgeApp()) {
            // 브릿지앱은 KioskActivity.onPageFinished()가 네이티브 전체화면을 복원한다.
            // 이전 웹 버전의 플래그가 남아 있어도 터치 팝업을 띄우지 않는다.
            localStorage.removeItem(UPDATE_NOTICE_KEY);
            localStorage.removeItem(LEGACY_FULLSCREEN_KEY);
          } else {
            // PC 브라우저는 새로고침 후 안내만 표시하며 전체화면 전환은 시도하지 않는다.
            localStorage.setItem(UPDATE_NOTICE_KEY, 'true');
          }
          window.location.reload();
        }
      } catch {
        // 네트워크 오류 무시
      }
    };

    fetchVersion();
    timer = setInterval(fetchVersion, CHECK_INTERVAL);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return buildTime;
}
